import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, extractApiError } from "../lib/api";
import { safeFetch } from "../lib/safeFetch";

const AuthContext = createContext(null);

// ── WCKT auto-cashout kill switch (frontend, 2026-06-27) ──────────────────
// Per operator decision: a player's 9-Wickets (WCKT transfer-wallet) balance
// must NEVER return to the site wallet without an EXPLICIT user click on the
// "Refresh" button in the header. Five different automatic paths existed
// historically — all gated by this single flag.
//
//  ❌ App-load silent cashout (login / mount with WCKT balance)
//  ❌ beforeunload / pagehide cashout (player closes browser tab)
//  ❌ Popup-close watcher (player closes the 9-Wickets game window)
//  ❌ Tab visibility-change cashout (player returns to maxlive88 tab)
//  ❌ Logout pre-drain (player clicks logout)
//
// Only the manual `refreshGameWallet()` flow (POST /api/wallet/game/refresh
// triggered by the 🔁 button) is allowed to pull funds. Flip this flag to
// `true` ONLY with written operator approval.
const WCKT_AUTO_CASHOUT_ENABLED = false;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  // In-flight guards for WCKT silent cashouts — prevents the 10s auto-pull
  // timer from stacking concurrent requests if DST is slow (which would
  // otherwise pile up fetch promises in memory and could, under sustained
  // DST outage, leak handles / slow the UI thread).
  const wcktAutopullInFlightRef = useRef(false);
  const wcktAppLoadFiredRef = useRef(false);
  // Track the last credit-toast fingerprint (label + ts) so back-to-back
  // duplicate WebSocket pushes for the SAME cashout never produce two
  // toasts. The id-based sonner dedup alone isn't enough because two
  // onmessage callbacks can fire inside the same microtask before sonner
  // has rendered the first toast (ID isn't yet in the toast queue → no
  // dedup → two toasts stack). The ref check is synchronous and catches
  // every duplicate regardless of render timing.
  const wcktLastCreditToastRef = useRef({ label: "", ts: 0 });

  const refreshUser = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch (err) {
      // Only clear the session if the server explicitly says we're unauthorized.
      // Network blips / 5xx / timeouts must NOT auto-logout the user.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        try { localStorage.removeItem("access_token"); sessionStorage.removeItem("access_token"); } catch { /* ignore */ }
        setUser(null);
      } else if (!silent) {
        setUser(null);
      }
      return null;
    }
  }, []);

  // Manual "Refresh balance" trigger used by the two balance-refresh buttons
  // (top navbar + wallet/dashboard). Unlike the automatic visibility-based
  // cashout in the effect below, this one runs UNCONDITIONALLY — so even if
  // the auto-cashout was skipped (popup ref lost, browser killed the tab,
  // user navigated away before the visibility event fired, etc.), the player
  // can just hit Refresh to pull their WICKET balance back to the site wallet.
  const refreshBalanceWithCashout = useCallback(async () => {
    let cashedOut = 0;
    const startedAt = Date.now();
    try {
      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const res = await safeFetch(`${process.env.REACT_APP_BACKEND_URL}/api/dst/wckt/cashout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Manual refresh: bypass the backend active-session guard. The
          // user explicitly clicked Refresh, so they want a full cashout
          // even mid-game. (Auto-pull / popup watcher do NOT set this
          // header, so they get gracefully skipped during active play.)
          "X-Cashout-Force": "1",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        timeoutMs: 15000,
      });
      const data = await res.json().catch(() => ({}));
      // ── Generic balance refresh toast — no internal provider names ──
      if (data?.ok && Number(data.withdrawn || 0) > 0) {
        cashedOut = Number(data.withdrawn);
        toast.success(`Balance updated · ৳${cashedOut.toFixed(2)} added`,
          { id: "wallet-refresh", duration: 3500 });
        // Clear any stale active-session marker since cashout succeeded.
        try { sessionStorage.removeItem("wckt_active_session"); } catch { /* ignore */ }
        try { window.__wcktPopup = null; } catch { /* ignore */ }
      } else if (data && data.ok === false && data.code === "SUSPICIOUS") {
        // Backend's sanity guard tripped — admin reviews. Keep this
        // message clear because the player NEEDS to know there's a hold.
        toast.error(
          "Your balance refresh is being reviewed by our security team for your safety. " +
          "Please contact Live Chat or Telegram support with your username — we'll release it shortly.",
          { id: "balance-suspicious", duration: 15000 }
        );
      } else if (data && data.ok === true && data.active_bet_locked && data.message) {
        // Active-bet escrow — money is locked in an open 9 Wickets bet.
        // This is NORMAL — don't show "Couldn't refresh" error. Show a
        // friendly informational message so player knows it'll auto-return.
        toast(data.message, { id: "wallet-bet-locked", duration: 9000, icon: "🏏" });
      } else if (data && data.ok === false && data.message && !data.queued_for_retry) {
        // Generic error — don't leak provider names.
        toast.error("Couldn't refresh balance — please try again.");
      } else if (data && data.ok === true && data.pending && data.message) {
        // Stuck-balance case: WCKT side reported funds but withdraw was
        // declined (login failure leaves a "ghost" deposit). Show the
        // backend's explicit message so the user knows it's not just a
        // simple "try again" issue — they may need Live Support help.
        toast.error(data.message, { id: "wallet-stuck", duration: 9000 });
      }
      // ── DST health heuristic (toast-only, no header pill) ─────────────
      // Track recent cashout durations. If 3+ requests in the last 60s took
      // longer than 8s OR returned ok:false, we infer DST is currently slow
      // and surface a single gentle one-time toast so the player understands
      // why their balance feels "stuck". Resets after 5 minutes of silence.
      try {
        const slow = Date.now() - startedAt > 8000;
        const failed = data?.ok === false && !data?.code; // network-level fail signal
        if (slow || failed) {
          const log = JSON.parse(sessionStorage.getItem("wckt_health_log") || "[]");
          log.push(Date.now());
          const cutoff = Date.now() - 60000;
          const recent = log.filter((t) => t >= cutoff);
          sessionStorage.setItem("wckt_health_log", JSON.stringify(recent));
          const lastWarnAt = Number(sessionStorage.getItem("wckt_health_warned_at") || 0);
          if (recent.length >= 3 && Date.now() - lastWarnAt > 5 * 60 * 1000) {
            sessionStorage.setItem("wckt_health_warned_at", String(Date.now()));
            toast.info(
              "Balance sync is taking a moment right now. Your balance will update automatically — no need to keep refreshing.",
              { id: "wallet-slow", duration: 8000 }
            );
          }
        } else {
          // Successful fast response — clear the slow-log so we don't warn
          // on stale data from an outage that already resolved.
          sessionStorage.removeItem("wckt_health_log");
        }
      } catch { /* sessionStorage disabled — ignore */ }
    } catch (e) {
      // Silent — refreshUser will still happen
      console.debug("[manual WCKT cashout]", e);
    }
    const u = await refreshUser();
    return { user: u, cashed_out: cashedOut };
  }, [refreshUser]);

  // ═══════════════════════════════════════════════════════════════════════
  // DUAL-WALLET REFRESH SYSTEM (2026-06-24)
  // Two distinct flows, each backed by its own server endpoint with
  // idempotency + locking + status tracking.
  //   • refreshMainWallet — reads site balance only (zero external calls)
  //   • refreshGameWallet — syncs 9-Wickets/WCKT + auto-cashout
  // Each click generates a unique Idempotency-Key (uuid v4-ish via crypto)
  // so accidental double-clicks NEVER trigger duplicate processing.
  // ═══════════════════════════════════════════════════════════════════════
  const _newIdemKey = useCallback(() => {
    try {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    } catch { /* fall through */ }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // applyWcktLaunchOptimistic — used by useGameLaunch right after a
  // successful 9-Wickets (WCKT) launch response from /api/dst/launch.
  //
  // PROBLEM IT SOLVES (mobile-only "double balance" bug):
  //   When a player taps a WCKT game tile on a mobile browser, the new tab
  //   opens immediately. Mobile browsers (Chrome Android / Safari iOS)
  //   aggressively suspend or fully kill the original site tab once the
  //   game tab becomes the foreground. The backend's WebSocket balance-push
  //   ("৳500 → ৳0") fires DURING that suspension window — so when the
  //   player returns to the site tab, they see the stale pre-deposit
  //   balance plus the new WCKT-side balance → looks like money doubled.
  //
  //   On desktop the WebSocket stays connected (background tab not
  //   suspended), so the push lands and the balance updates live. Hence
  //   the desktop-only correctness.
  //
  // FIX:
  //   Optimistically update the local user object on the foreground tab
  //   the moment we know the deposit will succeed (backend returned
  //   success + open_in_new_tab + deposited_amount). When the player
  //   returns to a still-alive (just suspended) site tab, the UI already
  //   shows the correct numbers; if the tab was killed entirely, the
  //   normal /auth/me bootstrap on next mount picks up the canonical
  //   state from the DB (which is also correct).
  // ═══════════════════════════════════════════════════════════════════════
  const applyWcktLaunchOptimistic = useCallback((depositedAmount) => {
    const amt = Number(depositedAmount) || 0;
    if (amt <= 0) return;
    setUser((cur) => {
      if (!cur) return cur;
      const prevBalance = Number(cur.balance) || 0;
      const newBalance = Math.max(0, prevBalance - amt);
      const prevKnown = Number(cur.wckt_known_balance) || 0;
      return {
        ...cur,
        balance: newBalance,
        wckt_known_balance: prevKnown + amt,
        wckt_known_balance_at: new Date().toISOString(),
      };
    });
  }, []);

  const refreshMainWallet = useCallback(async () => {
    const idem = _newIdemKey();
    try {
      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const res = await safeFetch(`${process.env.REACT_APP_BACKEND_URL}/api/wallet/main/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": idem,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        timeoutMs: 8000,
      });
      const data = await res.json().catch(() => ({}));
      if (data?.ok) {
        // Silent success — balance figure updates via refreshUser() below.
        // No toast needed for the local-only fast read.
      } else if (data?.message) {
        toast.error("Couldn't refresh balance — please try again.");
      }
      // Always reconcile the in-memory user object with the new balance.
      await refreshUser({ silent: true });
      return data;
    } catch (e) {
      console.debug("[refreshMainWallet]", e);
      toast.error("Couldn't refresh balance — please try again.");
      return { ok: false, error: String(e).slice(0, 200) };
    }
  }, [refreshUser, _newIdemKey]);

  const refreshGameWallet = useCallback(async () => {
    const idem = _newIdemKey();
    const startedAt = Date.now();
    try {
      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const res = await safeFetch(`${process.env.REACT_APP_BACKEND_URL}/api/wallet/game/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": idem,
          // Manual click on game-refresh button bypasses the backend
          // active-session guard (same as the /dst/wckt/cashout button).
          "X-Cashout-Force": "1",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        timeoutMs: 15000,
      });
      const data = await res.json().catch(() => ({}));
      const withdrawn = Number(data?.withdrawn || 0);
      const recoveryQueued = Boolean(data?.recovery_queued);
      const betsPending = Boolean(data?.bets_pending);
      // ── Generic, user-friendly toasts (no mention of 9-Wickets / DST) ──
      // Per product decision: users should NEVER see internal provider
      // names. The dual refresh buttons feel like a single seamless
      // "refresh balance" action. System silently syncs everything.
      if (data?.status === "SUCCESS" && withdrawn > 0) {
        // Distinguish a normal cashout from an auto-refund (failed launch
        // recovery) so the player understands what happened.
        if (data?.auto_refunded) {
          toast.success(
            data.message || `Balance updated · ৳${withdrawn.toFixed(2)} returned`,
            { id: "wallet-refresh", duration: 5500 }
          );
        } else if (data?.active_bet_locked) {
          // Partial withdrawal because some money is locked in an open bet.
          toast.success(
            data.message || `Balance updated · ৳${withdrawn.toFixed(2)} added`,
            { id: "wallet-refresh", duration: 7000 }
          );
        } else {
          toast.success(`Balance updated · ৳${withdrawn.toFixed(2)} added`,
            { id: "wallet-refresh", duration: 3500 });
        }
      } else if (data?.post_refund_locked && data?.message) {
        // Post-refund safety lock — previous deposit was already refunded.
        // Show a friendly informational toast (not a scary error).
        toast(data.message, { id: "wallet-already-refunded", duration: 7000, icon: "✓" });
      } else if (data?.in_progress && data?.message) {
        // Inner impl reports it's still working — DST is syncing.
        toast(data.message, { id: "wallet-syncing", duration: 5000, icon: "⏳" });
      } else if (data?.active_bet_locked && data?.message) {
        // Nothing was withdrawn because all of it is in escrow. Inform
        // the user — they should NOT see a "Couldn't refresh" toast.
        // Use a neutral "info"-style toast (default sonner toast).
        toast(data.message, { id: "wallet-bet-locked", duration: 9000, icon: "🏏" });
      } else if (data?.needs_admin_review && data?.message) {
        // Ghost-void: deposit didn't fully complete on provider side
        // (e.g. game login failure). Backend has flagged for admin review.
        // Tell user explicitly so they don't keep mashing refresh.
        toast.error(data.message, { id: "wallet-stuck", duration: 12000 });
      } else if (data?.ok === true && data?.pending && data?.message) {
        // Stuck-balance / ghost-void: provider reported funds but withdraw
        // failed. Show the explicit message so the user can contact support
        // instead of mashing the refresh button.
        toast.error(data.message, { id: "wallet-stuck", duration: 9000 });
      } else if (betsPending) {
        // Sportsbook: bets are still being settled by the match result.
        // Tell the user clearly so they don't keep mashing refresh.
        toast.message("Your bets are still being settled. Any winnings will be added automatically once the match results are in.",
          { id: "wallet-refresh", duration: 7000 });
      } else if (recoveryQueued) {
        // Backend has detected funds pending in the upstream provider and
        // queued them for guaranteed retrieval. Reassure the user the money
        // is safe and coming back automatically.
        toast.message("Balance is syncing — your funds will return automatically within a minute. No need to keep refreshing.",
          { id: "wallet-refresh", duration: 6500 });
      } else if (data?.status === "SUCCESS") {
        // Silent success — no toast needed if nothing changed. The visible
        // balance figure already updates via the refreshUser() call below.
      } else if (data?.status === "PENDING" || data?.in_progress) {
        toast.message("Balance sync is taking a moment — your balance will update automatically.",
          { id: "wallet-refresh", duration: 5000 });
        // ─── BACKGROUND POLL (2026-06-28 single-tap fix) ────────────────
        // Previously the user had to tap "Refresh" 2-3 times before the
        // balance arrived: tap #1 returned PENDING, server-side auto-
        // recovery worker (every 10s) eventually credited, but the UI
        // only re-checked on the NEXT explicit refresh tap. Now after a
        // PENDING response we silently poll /auth/me every 2 seconds for
        // up to 30 seconds. The moment the balance increases we show a
        // success toast and stop polling — making single-tap UX feel
        // instant even when DST is slow.
        (async () => {
          try {
            const baseSnap = await refreshUser({ silent: true });
            const baseBal = Number(baseSnap?.balance) || 0;
            const pollStart = Date.now();
            const poll = async () => {
              if (Date.now() - pollStart > 30000) return; // 30s budget
              try {
                const fresh = await refreshUser({ silent: true });
                const newBal = Number(fresh?.balance) || 0;
                if (newBal > baseBal + 0.01) {
                  const gained = newBal - baseBal;
                  toast.success(`Balance updated · ৳${gained.toFixed(2)} added`,
                    { id: "wallet-refresh", duration: 4000 });
                  return;
                }
              } catch { /* ignore network blip */ }
              setTimeout(poll, 2000);
            };
            setTimeout(poll, 2000); // first probe 2s after PENDING toast
          } catch { /* best-effort */ }
        })();
      } else if (data?.status === "FAILED") {
        toast.error("Couldn't refresh balance — please try again.");
      }
      const tookMs = Date.now() - startedAt;
      if (tookMs > 9000) {
        toast.message("Sync is taking a moment — your balance will update automatically.",
          { id: "wallet-slow", duration: 5000 });
      }
      await refreshUser({ silent: true });
      return data;
    } catch (e) {
      console.debug("[refreshGameWallet]", e);
      toast.error("Couldn't refresh balance — please try again.");
      return { ok: false, error: String(e).slice(0, 200) };
    }
  }, [refreshUser, _newIdemKey]);

  // Pre-withdraw guard — call this before navigating to /profile/withdraw.
  // Returns the final synced balance + whether withdrawal is allowed.
  const withdrawPrecheck = useCallback(async () => {
    const idem = _newIdemKey();
    try {
      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const res = await safeFetch(`${process.env.REACT_APP_BACKEND_URL}/api/wallet/withdraw-precheck`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": idem,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        timeoutMs: 15000,
      });
      const data = await res.json().catch(() => ({}));
      const w = Number(data?.withdrawn_during_precheck || 0);
      if (w > 0) {
        toast.success(`Balance updated · ৳${w.toFixed(2)} added`,
          { id: "wallet-refresh", duration: 3500 });
      }
      await refreshUser({ silent: true });
      return data;
    } catch (e) {
      console.debug("[withdrawPrecheck]", e);
      return { ok: false, error: String(e).slice(0, 200) };
    }
  }, [refreshUser, _newIdemKey]);

  useEffect(() => {
    refreshUser().finally(() => setChecking(false));
  }, [refreshUser]);

  // ── Layer #1 (app-load silent cashout) + #3 (beforeunload safety-net) ──
  // Triggered ONCE per `user.id` transition (login, refresh-restored session,
  // initial mount with persisted token). If WICKET still holds money for this
  // player (`wckt_known_balance > 0`), fire a single fire-and-forget cashout
  // immediately so the player never has to click anything. The WebSocket
  // balance-push handler shows the success toast when the credit lands.
  //
  // The `beforeunload` listener catches the "player just closed the tab"
  // case using fetch with `keepalive: true` — a modern browser API designed
  // exactly for this scenario (the request survives page-unload reliably,
  // unlike a normal fetch which the browser would cancel).
  useEffect(() => {
    if (!user?.id) return;
    const token = (() => {
      try { return localStorage.getItem("access_token") || sessionStorage.getItem("access_token"); }
      catch { return null; }
    })();
    if (!token) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/dst/wckt/cashout`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    // #1 — Silent cashout on app load if WICKET balance is sitting
    // One-shot per session: ref guard prevents this effect from firing
    // multiple cashouts if `wckt_known_balance` mutates rapidly during a
    // single login (e.g. WS push updates the field mid-flight).
    //
    // CRITICAL: We do NOT send X-Cashout-Force here. Sending force would
    // auto-cashout the player's money while they're still actively playing
    // in the 9-Wickets game tab (which is a separate browser tab/window
    // — refreshing our main maxlive88 site does not mean the player has
    // stopped playing). The backend's `wckt_active_session_until` guard
    // correctly skips the cashout for 10 minutes after launch, returning
    // a benign "active session" response. After 10 minutes (or after the
    // player closes the game tab and our popup-watcher fires with force),
    // the money pulls back automatically.
    //
    // Bug history: previous code had `X-Cashout-Force: 1` here. This caused
    // a real production money-vanish UX: player launches → enters 9-Wickets
    // → tabs back to maxlive88 → balance auto-pulled to site → 9-Wickets
    // shows BDT 0.00. From the player's perspective, their balance "disappeared".
    if (WCKT_AUTO_CASHOUT_ENABLED && (user.wckt_known_balance || 0) > 0.01 && !wcktAppLoadFiredRef.current) {
      wcktAppLoadFiredRef.current = true;
      try {
        safeFetch(url, {
          method: "POST",
          headers, // no X-Cashout-Force — let backend's session guard work
          credentials: "include",
          timeoutMs: 15000,
        }).then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (data?.ok && Number(data.withdrawn || 0) > 0) {
            refreshUser({ silent: true });
          }
        }).catch(() => { /* silent — autopull timer will retry */ });
      } catch { /* ignore */ }
    }

    // #3 — beforeunload last-chance cashout (only fires when there's money to pull)
    const onBeforeUnload = () => {
      // Gated: when auto-cashout is disabled, do NOT fire on tab-close either.
      if (!WCKT_AUTO_CASHOUT_ENABLED) return;
      try {
        if ((user.wckt_known_balance || 0) <= 0.01) return;
        // `keepalive: true` makes fetch survive page-unload. Cannot read the
        // response (browser kills the tab), but the backend processes it
        // anyway. Result lands via WebSocket push next time the player visits.
        fetch(url, {
          method: "POST",
          headers: { ...headers, "X-Cashout-Force": "1" },
          credentials: "include",
          keepalive: true,
          body: "{}",
        }).catch(() => { /* ignore */ });
      } catch { /* ignore */ }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onBeforeUnload);
    };
  }, [user?.id, user?.wckt_known_balance, refreshUser]);

  // Auto-refresh balance/user every 25s while logged in + when tab becomes visible.
  // This ensures balance updates after deposits / bets / wins without a full reload.
  // Also: if the player has an open WCKT (transfer-wallet) game session, the
  // moment they return to our tab we auto-cashout — transfer the leftover
  // WCKT provider-wallet balance back to the site wallet so they never lose
  // track of money sitting inside the WICKET portal.
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => {
      refreshUser({ silent: true });
    }, 25000);

    const tryWcktAutoCashout = async () => {
      let session = null;
      try {
        const raw = sessionStorage.getItem("wckt_active_session");
        if (!raw) return;
        session = JSON.parse(raw);
      } catch { return; }
      // ── STRICT POPUP-CLOSED GUARD (2026-06-25 bug fix) ────────────────
      // Provider confirmed: "৯ উইকেট এর ভেতরে refresh icon চাপলে balance
      // 0 হয়ে যাচ্ছে এটি আমাদের code এর সমস্যা". Root cause: when the
      // user pressed the refresh icon inside the 9-Wickets popup, mobile
      // browsers (esp. Chrome / Samsung Internet) sometimes lose the
      // `window.__wcktPopup` reference during the in-popup navigation.
      // The OLD fallback "if popup is null and session is > 1.5s old,
      // fire cashout anyway" then triggered an unwanted withdraw while
      // the player was still actively playing.
      //
      // New rule: fire cashout ONLY when we have a popup reference AND
      // it is confirmed closed. If the reference is lost, we conservatively
      // do NOTHING here — the player can recover via the manual Refresh
      // button at any time (it sends X-Cashout-Force=1 and works in all
      // cases). Safer to require manual user click than to auto-yank a
      // live session's balance.
      let popup = null;
      try { popup = window.__wcktPopup || null; } catch { /* ignore */ }
      if (!popup) {
        // Reference lost — DO NOT fire cashout. Conservative choice.
        // User's balance stays in WCKT until they click manual Refresh
        // or close the game tab from a window with a valid reference.
        return;
      }
      if (!popup.closed) {
        // Popup still open — player is actively playing.
        return;
      }
      // popup exists AND popup.closed === true → user has genuinely
      // closed the 9-Wickets tab → safe to cashout.
      // Clear the flag IMMEDIATELY so a fast back-and-forth can't trigger
      // duplicate cashouts.
      try { sessionStorage.removeItem("wckt_active_session"); } catch { /* ignore */ }
      try { window.__wcktPopup = null; } catch { /* ignore */ }
      // ── Fire-and-forget cashout ──
      // DST itself takes 3-10s per round trip (out of our control), so we do
      // NOT block on the response. The backend pushes the new balance via
      // WebSocket the moment it completes, which triggers the toast + UI
      // update. To the user this feels "instant" because there's no spinner
      // waiting on DST — they see no loading state, then a "৳X returned"
      // toast appears when the WS message lands.
      (async () => {
        try {
          const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
          const res = await safeFetch(`${process.env.REACT_APP_BACKEND_URL}/api/dst/wckt/cashout`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              // Popup-watcher: game window was closed → user has stopped
              // playing → safe & desired to force a FULL cashout (pulls
              // back both principal and winnings, regardless of the
              // active-session flag which may not have expired yet).
              "X-Cashout-Force": "1",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: "include",
            timeoutMs: 15000,
          });
          // Toast is shown by the WebSocket balance-push handler (single source
          // of truth). We just log the response for diagnostics.
          // NOTE: "recovery_queued" and "queued_for_retry" toasts removed per
          // product decision — they were firing on every refresh even when the
          // player had no actual WICKET balance, confusing the user. The WS
          // push handler is the ONLY trustworthy signal that money landed.
          const data = await res.json().catch(() => ({}));
          if (data?.message && (data.message || "").toLowerCase().indexOf("empty") < 0) {
            console.debug("[WCKT cashout]", data);
          }
        } catch (e) {
          console.debug("[WCKT cashout error]", e);
        } finally {
          // Belt-and-suspenders refresh in case the WS missed the push.
          refreshUser({ silent: true });
        }
      })();
      // Return synchronously — the visibility/popup-watcher path can fire
      // again immediately if needed (no in-flight blocking).
      return;
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refreshUser({ silent: true });
        // Gated: visibility change no longer triggers auto-cashout. User must
        // click the manual Refresh button to pull WCKT balance back to site.
        if (WCKT_AUTO_CASHOUT_ENABLED) {
          tryWcktAutoCashout();
        }
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    // MOBILE-CRITICAL — pageshow / pagehide events. Mobile browsers
    // (especially Safari iOS) restore the page from the back-forward cache
    // (bfcache) WITHOUT firing visibilitychange. The pageshow event with
    // `event.persisted = true` is the canonical signal that the page was
    // resurrected from bfcache after the player tabbed back from the WCKT
    // game tab. Without handling this, mobile users keep seeing the stale
    // pre-deposit balance until they tap somewhere or pull-to-refresh.
    const onPageShow = (ev) => {
      // Re-pull canonical balance whenever we're shown — covers bfcache
      // restore AND first paint. Cost is one /auth/me hit, fully idempotent.
      refreshUser({ silent: true });
    };
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("resume", onVisible);  // Cordova/PWA wrap (no-op in browser)

    // ── Active WCKT popup-close watcher (B3 — adaptive interval) ─────────
    // Polls `window.__wcktPopup.closed` to detect when the player exits the
    // WICKET tab. Starts AGGRESSIVE (300ms) so the cashout fires within ~1s
    // of the close-click. After 2 minutes of the popup being open without
    // closing, the player is genuinely playing — back off to 2s polling to
    // save mobile battery + reduce CPU drain. Auto-shuts-down after 90 min.
    //
    // GATED (2026-06-27): when WCKT_AUTO_CASHOUT_ENABLED is false, the
    // watcher does NOT schedule. The player must click the manual Refresh
    // button after closing the 9-Wickets tab — auto-pull is forbidden.
    const watcherStartedAt = Date.now();
    let watcherIntervalMs = 300;
    let popupWatcher = null;
    const _scheduleWatcher = () => {
      if (!WCKT_AUTO_CASHOUT_ENABLED) return;
      if (popupWatcher) clearInterval(popupWatcher);
      popupWatcher = setInterval(() => {
        if (Date.now() - watcherStartedAt > 90 * 60 * 1000) {
          clearInterval(popupWatcher);
          return;
        }
        // Bump interval to 2s after the popup has been open for 2 minutes
        // (steady state — player is actually playing).
        if (watcherIntervalMs === 300 && Date.now() - watcherStartedAt > 2 * 60 * 1000) {
          watcherIntervalMs = 2000;
          _scheduleWatcher();
          return;
        }
        let popup = null;
        try { popup = window.__wcktPopup || null; } catch { /* ignore */ }
        if (!popup) return;            // no active session
        if (!popup.closed) return;     // still open — player is still playing
        // Closed! Drop the reference and trigger cashout right now.
        try { window.__wcktPopup = null; } catch { /* ignore */ }
        tryWcktAutoCashout();
      }, watcherIntervalMs);
    };
    _scheduleWatcher();

    // ── BALANCE-RECONCILE POLL (2026-06-28 mobile fix) ─────────────────
    // Every 10s, if the player has an active WCKT session (i.e. their
    // wckt_known_balance > 0), silently re-fetch /auth/me to keep the
    // canonical balance in sync. This is the LAST-LINE defense for mobile
    // browsers that:
    //   • Kill the WebSocket connection in suspended tabs
    //   • Skip visibilitychange + pageshow events
    //   • Restore the page from a stale bfcache snapshot
    // Cost is one cheap /auth/me hit every 10s — fully safe (read-only,
    // no money movement). Auto-stops when wckt_known_balance returns to 0
    // (cashout complete) because the next effect re-run with the fresh
    // dependency value will skip scheduling the timer.
    let reconcilePoll = null;
    if ((user?.wckt_known_balance || 0) > 0.01) {
      reconcilePoll = setInterval(() => {
        if (document.visibilityState === "visible") {
          refreshUser({ silent: true });
        }
      }, 10000);
    }

    // ── AUTO-PULL DISABLED (2026-06-25) ────────────────────────────────
    // Provider CS confirmed: "Within 1 minute of credit transfer, all
    // credits are being withdrawn from your side. This is a problem."
    // Since DST callback URL is configured, the provider handles bet/
    // settle events internally. The user's WICKET wallet balance is
    // queryable via /api/agent/getTransferProviderBalance and withdrawn
    // via /api/agent/transferProviderWithdraw — but ONLY when the user
    // explicitly clicks a refresh button (X-Cashout-Force: 1 header)
    // or closes the game popup. NO background auto-pull.
    const autoPullTimer = setInterval(() => {
      // intentionally empty — manual refresh only.
    }, 60 * 60 * 1000);   // dummy 1h interval, no-op body

    return () => {
      clearInterval(id);
      if (popupWatcher) clearInterval(popupWatcher);
      if (reconcilePoll) clearInterval(reconcilePoll);
      clearInterval(autoPullTimer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("resume", onVisible);
    };
    // Intentionally read `user` via stale closure inside autoPullTimer to
    // avoid recreating the 10s interval (and all its listeners) every time
    // the balance updates. Stale closure is safe because the conditional
    // checks `user?.wckt_known_balance` which goes to 0 the moment cashout
    // succeeds — the WS push handler clears the field via setUser inside
    // refreshUser, so the next interval tick sees the fresh value naturally.
  }, [user?.id, user?.wckt_known_balance, refreshUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Real-time site WebSocket: balance push + session-killed events ──
  // Replaces the need for client-side polling after cashouts/deposits/etc —
  // the server pushes `{type:"balance", balance:N}` the moment the user's
  // wallet changes. Auto-reconnects with exponential backoff up to 30s.
  useEffect(() => {
    if (!user?.id) return;
    let ws = null;
    let reconnectTimer = null;
    let pingTimer = null;
    let attempt = 0;
    let alive = true;

    const connect = () => {
      if (!alive) return;
      const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      if (!token) return;
      const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/i, "ws");
      const url = `${backend}/api/ws/site?token=${encodeURIComponent(token)}`;
      try { ws = new WebSocket(url); } catch { return; }

      ws.onopen = () => {
        attempt = 0;
        // Keep the connection warm with a 25s ping (the backend echoes "pong")
        pingTimer = setInterval(() => {
          try { ws && ws.readyState === 1 && ws.send("ping"); } catch { /* ignore */ }
        }, 25000);
      };

      ws.onmessage = (ev) => {
        let msg = null;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (!msg || typeof msg !== "object") return;
        if (msg.type === "balance" && typeof msg.balance === "number") {
          // Server pushed an updated balance — patch the user object in place
          // without re-fetching /auth/me. This is the path that makes WICKET
          // cashout reflect on the UI almost instantly.
          setUser((cur) => {
            if (!cur) return cur;
            // If a WCKT cashout/settlement just landed, surface the credit
            // amount as a toast so the player sees confirmation.
            const delta = Number(msg.balance) - Number(cur.balance || 0);
            const src = typeof msg.source === "string" ? msg.source : "";
            const isWcktCredit = /^wckt_/.test(src);
            // Distinguish credit-side sources (money coming BACK to site)
            // from debit-side sources (money LEAVING site → WCKT). Only the
            // credit sources should clear the WCKT-in-wallet snapshot.
            const isCreditSource = (
              src === "wckt_cashout"
              || src === "wckt_cashout_fast"
              || src === "wckt_cashout_delayed"
              || src === "wckt_settlement"
              || src === "wckt_auto_refund_ghost_void"
            );
            const isDebitSource = (
              src === "wckt_deposit"
            );
            if (isWcktCredit && delta > 0.01 && isCreditSource) {
              const label = src === "wckt_settlement"
                ? "৳" + delta.toFixed(2) + " WICKET winnings credited"
                : "৳" + delta.toFixed(2) + " returned from WICKET";
              try {
                // ── DEDUP (2026-06-30) ────────────────────────────────────
                // Backend can fire push_balance for the SAME cashout from
                // multiple paths (fast-path + retry-worker reconcile, or
                // wckt_cashout + wckt_cashout_delayed, etc.). Two paths to
                // dedup, applied together for bullet-proof coverage:
                //   1. SYNCHRONOUS ref check — if the exact same label was
                //      shown within the last 4 seconds, skip entirely.
                //      Catches the race where two onmessage callbacks run
                //      in the same microtask before sonner has rendered.
                //   2. SONNER id — same label → same id → if dedup-1 misses,
                //      sonner replaces the first toast in place.
                const _now = Date.now();
                const _last = wcktLastCreditToastRef.current || {};
                if (_last.label === label && (_now - (_last.ts || 0)) < 4000) {
                  // Duplicate credit push within 4s window — silently drop.
                } else {
                  wcktLastCreditToastRef.current = { label, ts: _now };
                  toast.dismiss("wckt-recovery");
                  toast.success(label, { id: `wckt-credit:${label}` });
                }
              } catch { /* ignore */ }
            }
            // ── Patch user object ────────────────────────────────────
            // For DEPOSITS (balance going DOWN to WCKT), we must NOT clear
            // wckt_known_balance — it just got populated by the deposit.
            // Clearing it would hide the "money in 9 Wickets" pill and
            // make the player think their money vanished.
            // For CREDITS (cashouts/refunds), wckt_known_balance is now
            // truly 0 — clearing it is correct.
            const patch = { ...cur, balance: msg.balance };
            if (isCreditSource) {
              patch.wckt_known_balance = 0;
              patch.wckt_known_balance_at = "";
            } else if (isDebitSource && delta < -0.01) {
              // Money LEFT site → WCKT. Record the WCKT-side amount so the
              // floating pill renders immediately (delta is negative, so
              // -delta is the amount that went to WCKT).
              patch.wckt_known_balance = (Number(cur.wckt_known_balance) || 0) + (-delta);
              patch.wckt_known_balance_at = new Date().toISOString();
            }
            return patch;
          });
        } else if (msg.type === "session_killed") {
          try { localStorage.removeItem("access_token"); sessionStorage.removeItem("access_token"); } catch { /* ignore */ }
          setUser(null);
        }
      };

      ws.onclose = () => {
        if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
        if (!alive) return;
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s cap
        attempt += 1;
        const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(attempt, 5)));
        reconnectTimer = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        try { ws && ws.close(); } catch { /* ignore */ }
      };
    };

    connect();

    return () => {
      alive = false;
      if (pingTimer) clearInterval(pingTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws && ws.close(); } catch { /* ignore */ }
    };
  }, [user?.id]);

  const login = async (username, password, opts = {}) => {
    const { remember = true } = opts;
    try {
      const { data } = await api.post("/auth/login", { username, password });
      if (data.requires_2fa) {
        // Stash the remember choice so the 2FA step (separate API call) uses
        // the same storage policy when it eventually persists the token.
        try { sessionStorage.setItem("ml_remember", remember ? "1" : "0"); } catch { /* ignore */ }
        return { ok: true, requires_2fa: true, challenge_token: data.challenge_token, username: data.username };
      }
      if (data.access_token) {
        // Persistent ("Remember me") → localStorage; otherwise sessionStorage.
        const store = remember ? localStorage : sessionStorage;
        const other = remember ? sessionStorage : localStorage;
        store.setItem("access_token", data.access_token);
        try { other.removeItem("access_token"); } catch { /* ignore */ }
      }
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: extractApiError(e) };
    }
  };

  const verify2FA = async ({ challenge_token, totp_code, backup_code }) => {
    try {
      const { data } = await api.post("/auth/2fa-verify", { challenge_token, totp_code, backup_code });
      if (data.access_token) {
        let remember = true;
        try { remember = sessionStorage.getItem("ml_remember") !== "0"; } catch { /* ignore */ }
        const store = remember ? localStorage : sessionStorage;
        const other = remember ? sessionStorage : localStorage;
        store.setItem("access_token", data.access_token);
        try { other.removeItem("access_token"); } catch { /* ignore */ }
        try { sessionStorage.removeItem("ml_remember"); } catch { /* ignore */ }
      }
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: extractApiError(e) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: extractApiError(e) };
    }
  };

  const quickRegister = async (referCode = null) => {
    try {
      const payload = referCode ? { refer_code: referCode } : {};
      const { data } = await api.post("/auth/quick-register", payload);
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      setUser(data.user);
      return {
        ok: true,
        username: data.generated_username,
        password: data.generated_password,
        bonus: data.registration_bonus,
      };
    } catch (e) {
      return { ok: false, error: extractApiError(e) };
    }
  };

  const logout = async () => {
    // ── Layer #2: pre-logout WICKET drain (GATED) ────────────────────────
    // Previously: if money was sitting in the WICKET provider wallet on
    // logout, we did one last cashout so the player never lost sight of
    // their balance. Per operator decision (2026-06-27), even this safety
    // drain is now off — the player must click the manual Refresh button
    // BEFORE logging out to pull WCKT funds back. If they forget, the
    // funds sit safely in WCKT until the next login (the balance probe
    // endpoint /api/dst/wckt/balance still returns the real amount).
    if (WCKT_AUTO_CASHOUT_ENABLED) {
      try {
        if ((user?.wckt_known_balance || 0) > 0.01) {
          const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
          if (token) {
            await Promise.race([
              safeFetch(`${process.env.REACT_APP_BACKEND_URL}/api/dst/wckt/cashout`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                credentials: "include",
                timeoutMs: 5000,
              }).catch(() => null),
              new Promise((resolve) => setTimeout(resolve, 5000)),
            ]);
          }
        }
      } catch { /* ignore — don't block logout on cashout failure */ }
    }
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("access_token");
    sessionStorage.removeItem("access_token");
    setUser(null);
    // Reset the one-shot app-load fire guard so the next user who logs in
    // on this same tab can again trigger the silent cashout on mount.
    wcktAppLoadFiredRef.current = false;
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, verify2FA, register, quickRegister, logout, refreshUser, refreshBalanceWithCashout, refreshMainWallet, refreshGameWallet, withdrawPrecheck, applyWcktLaunchOptimistic }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
